import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Bike, Sparkles, Truck, MapPin, Phone, type LucideIcon } from "lucide-react";
import { trackCtaReservation, trackPhoneCallClick } from "@/lib/gtm";
import logo from "@/assets/logo-ebike-co.avif";
import LanguageSwitcher from "@/i18n/LanguageSwitcher";
import { useLang } from "@/i18n/useLang";
import { localizedPath } from "@/i18n/localizedPath";

const isCurrentlyOpen = (): boolean => {
  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const time = hours * 60 + minutes;
  const month = now.getMonth();

  const isHauteSaison = month === 6 || month === 7;
  const isHiver = month >= 10 || month <= 2;

  if (day === 0) {
    if (isHauteSaison) return time >= 570 && time < 750;
    return false;
  }
  if (day === 6) return time >= 570 && time < 1050;
  if (day === 1 || day === 2) {
    if (isHiver) return false;
  }
  return (time >= 570 && time < 750) || (time >= 840 && time < 1110);
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const isOpen = useMemo(() => isCurrentlyOpen(), []);
  const { t } = useTranslation();
  const lang = useLang();

  const mainLinks: { label: string; href: string; icon?: LucideIcon }[] = [
    { label: t("navbar.home"), href: localizedPath("/", lang) },
    { label: t("navbar.packages"), href: localizedPath("/formule-location-velo-landes", lang), icon: Bike },
    { label: t("navbar.experiences"), href: localizedPath("/experiences", lang), icon: Sparkles },
    { label: t("navbar.delivery"), href: localizedPath("/livraison", lang), icon: Truck },
    { label: t("navbar.locations"), href: localizedPath("/etablissements", lang), icon: MapPin },
  ];

  const secondaryLinks = [
    { label: t("navbar.surf"), href: localizedPath("/location-surf-contis", lang), key: "surf" },
    { label: t("navbar.about"), href: localizedPath("/a-propos", lang), key: "about" },
    { label: t("navbar.pros"), href: localizedPath("/professionnels", lang), key: "pros" },
    { label: t("navbar.faq"), href: localizedPath("/faq", lang), key: "faq" },
    { label: t("navbar.blog"), href: "/blog", key: "blog" },
    { label: t("navbar.contact"), href: localizedPath("/contact", lang), key: "contact" },
  ];

  const bookHref = localizedPath("/reserver", lang);

  return (
    <>
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container-narrow flex items-center justify-between h-16 px-4 md:px-8 lg:px-16">
        <Link to={localizedPath("/", lang)} className="flex items-center gap-3">
          <img src={logo} alt="eBike & Co" width={40} height={40} className="h-10" />
          {isOpen && (
            <span className="hidden sm:inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {t("navbar.openNow")}
            </span>
          )}
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-6">
          {mainLinks.slice(1).map((l, i) => (
            <Link
              key={i}
              to={l.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </Link>
          ))}

          <div className="relative group">
            <button className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
              {t("navbar.more")} <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="absolute top-full right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              {secondaryLinks.map((l, i) => (
                <React.Fragment key={i}>
                  <Link
                    to={l.href}
                    className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  >
                    {l.label}
                    {l.key === "surf" && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">{t("navbar.new")}</span>
                    )}
                  </Link>
                  {l.key === "surf" && (
                    <div className="mx-3 my-1 border-t border-border" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <LanguageSwitcher />
          <Button size="sm" asChild>
            <Link to={bookHref} onClick={() => trackCtaReservation(t("navbar.book"), "navbar")}>{t("navbar.book")}</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-foreground"
          aria-label={open ? t("navbar.closeMenu") : t("navbar.openMenu")}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
    </nav>

    {/* Mobile menu */}
    {open && (
      <div className="fixed inset-0 top-16 lg:hidden bg-background z-[60] px-6 pb-8 pt-6 overflow-y-auto flex flex-col">
        <div className="flex flex-col gap-1">
          {mainLinks.filter((_, i) => i > 0).map((l, i) => (
            <Link
              key={i}
              to={l.href}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 py-3.5 px-4 text-foreground font-semibold text-base rounded-xl hover:bg-secondary transition-colors"
            >
              {l.icon && <l.icon className="w-5 h-5 text-muted-foreground" />}
              {l.label}
            </Link>
          ))}
        </div>

        <div className="h-px bg-border my-4" />

        <div className="flex flex-col gap-1">
          {secondaryLinks.filter(l => l.key !== "contact").map((l, i) => (
            <Link
              key={i}
              to={l.href}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between py-3 px-4 text-muted-foreground font-medium text-sm rounded-xl hover:bg-secondary hover:text-foreground transition-colors"
            >
              {l.label}
              {l.key === "surf" && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">{t("navbar.new")}</span>
              )}
            </Link>
          ))}
        </div>

        <div className="mt-auto pt-6 flex flex-col gap-3">
          <div className="flex justify-center pb-2">
            <LanguageSwitcher />
          </div>
          <Button className="w-full" size="lg" asChild>
            <Link to={bookHref} onClick={() => { setOpen(false); trackCtaReservation(t("navbar.bookNow"), "navbar-mobile"); }}>{t("navbar.bookNow")}</Link>
          </Button>
          <a
            href="tel:+33558427512"
            onClick={() => trackPhoneCallClick("+33558427512", "navbar-mobile")}
            className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            <Phone className="w-4 h-4" /> 05 58 42 75 12
          </a>
        </div>
      </div>
    )}
    </>
  );
};

export default Navbar;
